(() => {
  'use strict';

  const modules = window.PersonalCenterViewModules = window.PersonalCenterViewModules || {};
  // Every date the API returns is a store fact expressed as a UTC instant, so it is rendered
  // in the store's timezone rather than the visitor's.
  const STORE_TIME_ZONE = 'America/Mexico_City';
  const CREDIT_PAGE_SIZE = 8;
  const CREDIT_STATES = new Set(['available', 'pending', 'completed']);
  const CREDIT_TRANSACTION_FILTERS = new Set(['all', 'earned', 'used']);
  const COUPON_STATUSES = new Set(['available', 'used', 'expired']);
  const creditStates = new WeakMap();
  const fallbackCreditRoot = {};

  const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const asText = (value) => value == null ? '' : String(value).trim();
  const appendText = (parent, value) => parent.appendChild(document.createTextNode(asText(value)));
  const dataOf = (envelope) => isRecord(envelope) && envelope.success === true ? envelope.data : null;

  function loadMetadata(result, successCount, failures) {
    return Object.assign(result, {
      partial: successCount > 0 && failures.length > 0,
      failed: successCount === 0 && failures.length > 0,
      failures
    });
  }

  function failureFor(endpoint, reason) {
    const envelopeError = isRecord(reason) && isRecord(reason.error) ? reason.error : null;
    return {
      endpoint,
      code: asText(envelopeError?.code || reason?.code || 'APP_PROXY_UNAVAILABLE'),
      message: asText(envelopeError?.message || reason?.message)
    };
  }

  function throwIfAborted(signal) {
    if (signal?.aborted) {
      const error = new Error('Aborted');
      error.name = 'AbortError';
      throw error;
    }
  }

  function responseSucceeded(response) {
    return isRecord(response) && response.success === true;
  }

  function abortError() {
    try {
      return new DOMException('Credit request superseded by a later intent.', 'AbortError');
    } catch (_error) {
      const error = new Error('Credit request superseded by a later intent.');
      error.name = 'AbortError';
      return error;
    }
  }

  function creditStateFor(root) {
    const key = root && (typeof root === 'object' || typeof root === 'function') ? root : fallbackCreditRoot;
    let state = creditStates.get(key);
    if (!state) {
      state = {
        intent: 0,
        pageSize: CREDIT_PAGE_SIZE,
        includePending: true,
        transactionFilter: 'all',
        transactions: null,
        cursorHistory: [null],
        cursorIndex: 0,
        pageInfo: null
      };
      creditStates.set(key, state);
    }
    return state;
  }

  function creditRoot(root) {
    if (root?.matches?.('.pc-credits')) return root;
    return root?.querySelector?.('.pc-credits') || null;
  }

  function creditTransactionFilterFor(value) {
    const filter = tokenFor(value);
    return CREDIT_TRANSACTION_FILTERS.has(filter) ? filter : 'all';
  }

  async function loadCredits({ client, root, params = {}, signal }) {
    const state = creditStateFor(root);
    const cursor = asText(params.cursor) || null;
    const includePending = params.includePending !== false;
    const transactionFilter = creditTransactionFilterFor(params.transactionFilter);
    const intent = ++state.intent;
    state.pageSize = CREDIT_PAGE_SIZE;
    state.includePending = includePending;
    state.transactionFilter = transactionFilter;
    if (cursor === null) {
      state.cursorHistory = [null];
      state.cursorIndex = 0;
    } else {
      const knownIndex = state.cursorHistory.findIndex((knownCursor) => knownCursor === cursor);
      if (knownIndex >= 0) state.cursorIndex = knownIndex;
      else {
        state.cursorHistory = state.cursorHistory.slice(0, state.cursorIndex + 1);
        state.cursorHistory.push(cursor);
        state.cursorIndex = state.cursorHistory.length - 1;
      }
    }
    const transactionArgs = {
      pageSize: CREDIT_PAGE_SIZE,
      includePending,
      signal
    };
    if (cursor) transactionArgs.cursor = cursor;

    const requests = [
      ['summary', () => client.getCreditSummary({ signal })],
      ['balance', () => client.getCreditBalance({ signal })],
      ['transactions', () => client.listCreditTransactions(transactionArgs)],
      ['pending', () => client.getCreditPending({ signal })]
    ];
    const settled = await Promise.allSettled(requests.map(([, request]) => request()));
    throwIfAborted(signal);
    if (intent !== state.intent) throw abortError();

    const result = { summary: null, balance: null, transactions: null, pending: null };
    const failures = [];
    let successCount = 0;
    settled.forEach((outcome, index) => {
      const endpoint = requests[index][0];
      if (outcome.status === 'fulfilled') {
        result[endpoint] = outcome.value;
        if (responseSucceeded(outcome.value)) successCount += 1;
        else failures.push(failureFor(endpoint, outcome.value));
        return;
      }
      if (outcome.reason?.name === 'AbortError') throw outcome.reason;
      failures.push(failureFor(endpoint, outcome.reason));
    });

    return loadMetadata(result, successCount, failures);
  }

  async function loadCoupons({ client, params = {}, signal }) {
    const requestedStatus = asText(params.status || params.filter);
    const status = COUPON_STATUSES.has(requestedStatus) ? requestedStatus : undefined;
    try {
      const coupons = await client.listCoupons({ status, signal });
      throwIfAborted(signal);
      return loadMetadata({ coupons }, responseSucceeded(coupons) ? 1 : 0, responseSucceeded(coupons) ? [] : [failureFor('coupons', coupons)]);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return loadMetadata({ coupons: null }, 0, [failureFor('coupons', error)]);
    }
  }

  function setText(root, selector, value) {
    root.querySelectorAll(selector).forEach((node) => { node.textContent = asText(value) || '—'; });
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = hidden;
  }

  function formatMoney(money) {
    const amount = Number(money?.amount);
    const currencyCode = asText(money?.currencyCode);
    if (!Number.isFinite(amount) || !currencyCode) return '—';
    try {
      const formatted = new Intl.NumberFormat('es-MX', { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' }).format(amount);
      return `${formatted} ${currencyCode}`;
    } catch (_error) {
      return `${new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(amount)} ${currencyCode}`;
    }
  }

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return asText(value) || '—';
    /**
     * Fixed to the store's timezone, not the device's. These dates are store facts - when a
     * coupon opens, when it closes - and the API sends them as UTC instants. Formatting a
     * window that ends at 23:59:59 UTC-6 with a device set to Asia shows the following day,
     * so a shopper abroad would read a deadline a day later than the one that applies.
     */
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeZone: STORE_TIME_ZONE }).format(date);
  }

  function formatPercent(value) {
    const number = Number(value);
    return Number.isFinite(number) ? new Intl.NumberFormat('es-MX', { style: 'percent', maximumFractionDigits: 2 }).format(number) : '—';
  }

  function labelsFrom(root, selector) {
    return Array.from(root.querySelectorAll(selector)).reduce((labels, node) => {
      labels[node.dataset.pcWalletLabel] = node.textContent.trim();
      return labels;
    }, {});
  }

  function renderPartialDetail(root, response) {
    const failures = Array.isArray(response?.failures) ? response.failures : [];
    const diagnostic = failures.length
      ? failures.map(({ endpoint, code }) => `${endpoint}: ${code}`).join(' · ')
      : '';
    root.querySelectorAll('[data-pc-wallet-partial-detail]').forEach((detail) => {
      detail.textContent = detail.dataset.pcWalletPartialDefault || detail.textContent;
      if (diagnostic) detail.dataset.pcWalletPartialFailures = diagnostic;
      else delete detail.dataset.pcWalletPartialFailures;
    });
    root.querySelectorAll('[data-pc-wallet-retained-partial]').forEach((notice) => {
      setHidden(notice, !(response?.partial === true && failures.length > 0));
    });
  }

  function renderRetainedCreditContent(root, shouldRetain) {
    const partial = root.querySelector('[data-pc-state="partial"]');
    const current = partial?.querySelector('[data-pc-wallet-retained-content]');
    if (!shouldRetain || !partial) {
      current?.remove();
      partial?.classList.remove('pc-credits__state--with-retained');
      return;
    }

    const ready = root.querySelector('[data-pc-state="ready"]');
    if (!ready) return;
    const retained = ready.cloneNode(true);
    retained.className = 'pc-credits__retained-content';
    retained.hidden = false;
    retained.removeAttribute('data-pc-state');
    retained.removeAttribute('data-pc-state-output');
    retained.dataset.pcWalletRetainedContent = '';
    retained.querySelectorAll('[data-pc-wallet-retained-partial]').forEach((notice) => notice.remove());
    retained.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    retained.querySelectorAll('[aria-controls]').forEach((node) => node.removeAttribute('aria-controls'));
    retained.querySelectorAll('[data-pc-action], button').forEach((control) => {
      control.removeAttribute('data-pc-action');
      if ('disabled' in control) control.disabled = true;
    });

    const output = current || document.createElement('div');
    output.dataset.pcWalletRetainedContent = '';
    output.replaceChildren(retained);
    if (!current) partial.appendChild(output);
    partial.classList.add('pc-credits__state--with-retained');
  }

  function hasMoney(money) {
    return isRecord(money) && Number.isFinite(Number(money.amount)) && Boolean(asText(money.currencyCode));
  }

  function firstMoney(...candidates) {
    return candidates.find(hasMoney) || candidates.find(isRecord) || null;
  }

  function hasPendingContent(pending, summary) {
    const pendingTotal = firstMoney(pending?.pendingTotal, summary?.pending);
    const nextRelease = pending?.nextRelease || summary?.nextRelease;
    return hasMoney(pendingTotal)
      || (isRecord(nextRelease) && (hasMoney(nextRelease.amount) || Boolean(asText(nextRelease.availableAt)) || Boolean(asText(nextRelease.orderName))))
      || (Array.isArray(pending?.items) && pending.items.some(isRecord));
  }

  function hasTransactionContent(transactionsEnvelope) {
    const items = dataOf(transactionsEnvelope);
    return Array.isArray(items) && items.some((transaction) => isRecord(transaction) && (
      Boolean(asText(transaction.createdAt))
      || Boolean(asText(transaction.state))
      || Boolean(asText(transaction.concept))
      || Boolean(asText(transaction.orderName))
      || hasMoney(transaction.amount)
    ));
  }

  function creditsRenderState(response) {
    const endpointNames = ['summary', 'balance', 'transactions', 'pending'];
    const successCount = endpointNames.filter((name) => responseSucceeded(response?.[name])).length;
    if (response?.failed === true || successCount === 0) return 'error';
    if (response?.partial === true || successCount < endpointNames.length) return 'partial';

    const summary = dataOf(response?.summary);
    const balance = dataOf(response?.balance);
    const availableMoney = firstMoney(summary?.balance, balance?.balance);
    return hasMoney(availableMoney) || hasTransactionContent(response?.transactions) || hasPendingContent(dataOf(response?.pending), summary)
      ? 'ready'
      : 'empty';
  }

  function couponsRenderState(response) {
    if (!responseSucceeded(response?.coupons)) return 'error';
    const coupons = dataOf(response.coupons);
    return Array.isArray(coupons) && coupons.length > 0 ? 'ready' : 'empty';
  }

  function createCell(label, value, className = '') {
    const cell = document.createElement('td');
    cell.dataset.label = label;
    if (className) cell.className = className;
    appendText(cell, value || '—');
    return cell;
  }

  function tokenFor(value) {
    return asText(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function conceptText(concept, labels) {
    const raw = asText(concept);
    return labels[`concept-${tokenFor(raw)}`] || labels['concept-unknown'] || '—';
  }

  function amountDirection(transaction) {
    const direction = tokenFor(transaction?.direction);
    if (['credit', 'in', 'increase', 'earned', 'positive'].includes(direction)) return 'positive';
    if (['debit', 'out', 'decrease', 'spent', 'negative'].includes(direction)) return 'negative';
    const amount = Number(transaction?.amount?.amount);
    if (Number.isFinite(amount) && amount < 0) return 'negative';
    if (Number.isFinite(amount) && amount > 0) return 'positive';
    return 'neutral';
  }

  function filteredTransactions(transactions, filter) {
    const selectedFilter = creditTransactionFilterFor(filter);
    if (selectedFilter === 'all') return transactions;
    const direction = selectedFilter === 'earned' ? 'positive' : 'negative';
    return transactions.filter((transaction) => amountDirection(transaction) === direction);
  }

  function transactionEmptyText(filter, labels) {
    const selectedFilter = creditTransactionFilterFor(filter);
    return labels[`filter-empty-${selectedFilter}`] || labels.empty || '—';
  }

  function renderCreditTransactionFilters(root, filter) {
    const credits = creditRoot(root);
    const selectedFilter = creditTransactionFilterFor(filter);
    credits?.querySelectorAll('[data-pc-credit-filter]').forEach((control) => {
      const isPressed = asText(control.dataset.pcCreditFilter) === selectedFilter;
      control.setAttribute('aria-pressed', String(isPressed));
      control.classList.toggle('is-active', isPressed);
    });
    if (credits) credits.dataset.pcCreditTransactionFilter = selectedFilter;
  }

  function transactionAmountText(transaction) {
    const money = transaction?.amount;
    const amount = Number(money?.amount);
    const direction = amountDirection(transaction);
    if (!Number.isFinite(amount) || !hasMoney(money)) return formatMoney(money);
    if (direction === 'neutral') return formatMoney(money);
    const formatted = formatMoney(Object.assign({}, money, { amount: Math.abs(amount) }));
    return `${direction === 'negative' ? '−' : '+'}${formatted}`;
  }

  function availableAtText(value, labels) {
    const date = formatDate(value);
    const template = asText(labels['available-at']);
    return template ? template.replace('__DATE__', date) : date;
  }

  function renderTransactions(root, transactionsEnvelope, labels, filter) {
    const target = root.querySelector('[data-pc-transaction-list]');
    if (!target) return;
    target.replaceChildren();
    const transactions = dataOf(transactionsEnvelope);
    const items = Array.isArray(transactions) ? filteredTransactions(transactions, filter) : [];
    if (!responseSucceeded(transactionsEnvelope) || items.length === 0) {
      const row = document.createElement('tr');
      const cell = document.createElement('td');
      cell.colSpan = 5;
      cell.className = 'pc-credits__table-empty';
      appendText(cell, transactionEmptyText(filter, labels));
      row.appendChild(cell);
      target.appendChild(row);
      return;
    }

    items.forEach((transaction) => {
      const row = document.createElement('tr');
      const state = asText(transaction?.state);
      const stateClass = CREDIT_STATES.has(state) ? state : 'unknown';
      const stateLabel = CREDIT_STATES.has(state) ? (labels[`credit-${state}`] || labels['credit-unknown'] || '—') : (labels['credit-unknown'] || '—');
      const amountDirectionClass = amountDirection(transaction);
      const amount = createCell(labels.amount, transactionAmountText(transaction), 'pc-credits__amount-cell');
      if (amountDirectionClass !== 'neutral') amount.classList.add(`pc-credits__amount-cell--${amountDirectionClass}`);
      const status = document.createElement('span');
      status.className = `pc-credits__status pc-credits__status--${stateClass}`;
      status.dataset.pcCreditState = state;
      appendText(status, stateLabel);
      const statusStack = document.createElement('span');
      statusStack.className = 'pc-credits__status-stack';
      statusStack.appendChild(status);
      if (state === 'pending' && asText(transaction?.availableAt)) {
        const availableAt = document.createElement('small');
        availableAt.className = 'pc-credits__available-at';
        appendText(availableAt, availableAtText(transaction.availableAt, labels));
        statusStack.appendChild(availableAt);
      }
      const statusCell = createCell(labels.status, '');
      statusCell.replaceChildren(statusStack);
      const conceptCell = createCell(labels.concept, conceptText(transaction?.concept, labels));
      conceptCell.dataset.pcCreditConcept = asText(transaction?.concept);
      const orderName = asText(transaction?.orderName);
      const order = createCell(labels.order, orderName);
      order.dataset.pcCreditOrder = orderName ? 'present' : 'empty';
      row.append(
        createCell(labels.date, formatDate(transaction?.createdAt)),
        conceptCell,
        order,
        amount,
        statusCell
      );
      target.appendChild(row);
    });
  }

  function renderCreditPagination(root, transactionsEnvelope, labels) {
    const state = creditStateFor(root);
    const credits = creditRoot(root);
    const pagination = credits?.querySelector('[data-pc-credits-pagination]');
    const previous = pagination?.querySelector('[data-pc-page-direction="previous"]');
    const next = pagination?.querySelector('[data-pc-page-direction="next"]');
    const pageInfo = isRecord(transactionsEnvelope?.meta?.pageInfo) ? transactionsEnvelope.meta.pageInfo : null;
    state.pageInfo = pageInfo;
    const canNext = pageInfo?.hasNextPage === true && Boolean(asText(pageInfo?.nextCursor));
    const canPrevious = state.cursorIndex > 0;
    if (credits) {
      credits.dataset.pcCreditPageSize = asText(pageInfo?.pageSize || state.pageSize);
      credits.dataset.pcCreditPage = asText(state.cursorIndex + 1);
      credits.dataset.pcCreditTotal = asText(pageInfo?.total);
    }
    if (!pagination || !previous || !next) return;
    // Keep the paired arrow controls visible even for a single page so this
    // footer retains stable geometry as cursor availability changes.
    pagination.hidden = false;
    previous.disabled = !canPrevious;
    next.disabled = !canNext;
    previous.setAttribute('aria-disabled', String(!canPrevious));
    next.setAttribute('aria-disabled', String(!canNext));
  }

  function labelTemplate(labels, key, replacements) {
    let output = asText(labels[key]);
    Object.entries(replacements).forEach(([name, value]) => {
      output = output.replaceAll(`__${name}__`, asText(value));
    });
    return output;
  }

  function pendingReleaseText(release, labels) {
    if (!isRecord(release)) return labels.pendingCaption || '—';
    const parts = [];
    if (hasMoney(release.amount)) parts.push(formatMoney(release.amount));
    if (asText(release.availableAt)) parts.push(availableAtText(release.availableAt, labels));
    if (asText(release.orderName)) parts.push(`${labels.order || 'Pedido'} ${asText(release.orderName)}`);
    return parts.join(' · ') || labels.pendingCaption || '—';
  }

  function createPendingItem(item, labels) {
    const entry = document.createElement('li');
    entry.className = 'pc-credits__pending-item';
    entry.dataset.pcCreditPendingItem = '';

    const order = document.createElement('strong');
    order.dataset.pcCreditPendingOrder = '';
    appendText(order, asText(item?.orderName) || labels['pending-unknown-order'] || '—');
    entry.appendChild(order);

    if (hasMoney(item?.estimatedCredit)) {
      const estimated = document.createElement('span');
      estimated.dataset.pcCreditPendingEstimated = '';
      appendText(estimated, labelTemplate(labels, 'pending-estimated', { AMOUNT: formatMoney(item.estimatedCredit) }) || formatMoney(item.estimatedCredit));
      entry.appendChild(estimated);
    }

    if (asText(item?.releaseAt)) {
      const release = document.createElement('time');
      release.dataset.pcCreditPendingItemRelease = '';
      release.dateTime = asText(item.releaseAt);
      appendText(release, labelTemplate(labels, 'pending-release', { DATE: formatDate(item.releaseAt) }) || formatDate(item.releaseAt));
      entry.appendChild(release);
    }
    return entry;
  }

  function renderPendingDetails(root, pending, labels) {
    const section = root.querySelector('[data-pc-credit-pending-details]');
    const list = section?.querySelector('[data-pc-credit-pending-items]');
    const rulesOutput = section?.querySelector('[data-pc-credit-pending-rules]');
    const disclaimerOutput = section?.querySelector('[data-pc-credit-pending-disclaimer]');
    const items = Array.isArray(pending?.items) ? pending.items.filter(isRecord) : [];
    if (list) {
      list.replaceChildren();
      items.forEach((item) => list.appendChild(createPendingItem(item, labels)));
    }
    if (!section) return;
    section.hidden = items.length === 0;
    if (items.length === 0) {
      setHidden(rulesOutput, true);
      setHidden(disclaimerOutput, true);
      return;
    }

    const facts = [];
    const rateValue = pending?.rules?.rate;
    const holdDaysValue = pending?.rules?.holdDays;
    const rate = rateValue == null || asText(rateValue) === '' ? NaN : Number(rateValue);
    const holdDays = holdDaysValue == null || asText(holdDaysValue) === '' ? NaN : Number(holdDaysValue);
    if (Number.isFinite(rate)) facts.push(labelTemplate(labels, 'pending-rate', { RATE: formatPercent(rate) }));
    if (Number.isFinite(holdDays)) facts.push(labelTemplate(labels, 'pending-hold-days', { DAYS: holdDays }));
    if (rulesOutput) {
      rulesOutput.textContent = facts.filter(Boolean).join(' · ');
      rulesOutput.hidden = facts.length === 0;
    }

    const disclaimer = asText(pending?.disclaimer);
    if (disclaimerOutput) {
      disclaimerOutput.textContent = disclaimer;
      disclaimerOutput.hidden = !disclaimer;
    }
  }

  function renderPending(root, pending, summary, labels) {
    const pendingTotal = firstMoney(pending?.pendingTotal, summary?.pending);
    setText(root, '[data-pc-credit-pending-total]', formatMoney(pendingTotal));
    const release = pending?.nextRelease || summary?.nextRelease;
    const releaseOutput = root.querySelector('[data-pc-credit-pending-release]');
    if (releaseOutput) releaseOutput.textContent = pendingReleaseText(release, labels);
    renderPendingDetails(root, pending, labels);
  }

  function renderCredits({ root, response }) {
    const state = creditsRenderState(response);
    if (state === 'error' || state === 'empty') {
      renderPartialDetail(root, response);
      renderRetainedCreditContent(root, false);
      return { state };
    }
    const labels = labelsFrom(root, '[data-pc-wallet-label]');
    const creditState = creditStateFor(root);
    const summary = dataOf(response?.summary);
    const balance = dataOf(response?.balance);
    const pending = dataOf(response?.pending);
    const availableMoney = firstMoney(summary?.balance, balance?.balance);
    setText(root, '[data-pc-credit-available-total]', formatMoney(availableMoney));
    renderPending(root, pending, summary, labels);
    creditState.transactions = response?.transactions || null;
    renderCreditTransactionFilters(root, creditState.transactionFilter);
    renderTransactions(root, creditState.transactions, labels, creditState.transactionFilter);
    renderCreditPagination(root, response?.transactions, labels);
    renderPartialDetail(root, response);
    renderRetainedCreditContent(root, state === 'partial');
    return { state };
  }

  function couponValueText(value, labels) {
    if (!isRecord(value)) return '—';
    if (value.type === 'percentage') return formatPercent(value.percentage);
    if (value.type === 'amount') return formatMoney(value.amount);
    if (value.type === 'free_shipping') return labels['free-shipping'] || value.type;
    return asText(value.type) || '—';
  }

  function minimumPurchaseText(minimumPurchase, labels) {
    if (!isRecord(minimumPurchase)) return '—';
    if (hasMoney(minimumPurchase)) return formatMoney(minimumPurchase);
    if (minimumPurchase.type === 'subtotal') return formatMoney(minimumPurchase.amount);
    if (minimumPurchase.type === 'quantity') return Number.isFinite(Number(minimumPurchase.quantity)) ? `${minimumPurchase.quantity}` : '—';
    return asText(minimumPurchase.type) || '—';
  }

  function appliesToText(appliesTo) {
    if (!isRecord(appliesTo)) return '—';
    if (asText(appliesTo.text)) return asText(appliesTo.text);
    if (Array.isArray(appliesTo.names) && appliesTo.names.length) return appliesTo.names.map(asText).filter(Boolean).join(', ');
    return asText(appliesTo.scope) || '—';
  }

  function couponBenefitText(coupon, labels) {
    const title = asText(coupon?.title);
    if (title && !title.includes('\uFFFD')) return title;

    const value = couponValueText(coupon?.value, labels);
    const quantity = Number(coupon?.minimumPurchase?.quantity);
    const quantityThreshold = asText(labels['quantity-threshold']);
    if (value !== '—' && Number.isFinite(quantity) && quantity > 0 && quantityThreshold) {
      return `${value} ${quantityThreshold.replace('__COUNT__', String(quantity))}`.trim();
    }

    const summary = asText(coupon?.summary);
    if (summary && !summary.includes('\uFFFD')) return summary;
    return value;
  }

  /** True while the coupon exists but its validity window has not opened yet. */
  function couponNotStartedYet(coupon) {
    const value = asText(coupon?.startsAt);
    if (!value) return false;
    const starts = new Date(value);
    return !Number.isNaN(starts.getTime()) && starts.getTime() > Date.now();
  }

  function fillTemplate(template, values) {
    return Object.entries(values).reduce(
      (out, [key, value]) => out.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value),
      template
    );
  }

  /**
   * The date line under the coupon title.
   *
   * Showing only `endsAt` was actively misleading for the wheel prizes: they are created
   * SCHEDULED, so a shopper could hold a coupon that reads "válido hasta el 20 de
   * septiembre" while checkout refuses it for another ten days. The window has two ends and
   * the shopper needs both.
   */
  function couponExpiryText(coupon, status, labels) {
    const startsAt = coupon?.startsAt ? formatDate(coupon.startsAt) : '';
    const endsAt = coupon?.endsAt ? formatDate(coupon.endsAt) : '';

    if (status === 'expired' && endsAt) {
      const template = asText(labels['coupon-expired-on']);
      return template ? fillTemplate(template, { date: endsAt }) : endsAt;
    }

    // Not open yet: lead with when it opens, because that is the answer to "why can I not
    // use this". The closing date still rides along so the shopper knows the whole window.
    if (couponNotStartedYet(coupon)) {
      const template = asText(labels[endsAt ? 'coupon-valid-from-until' : 'coupon-valid-from']);
      if (template) return fillTemplate(template, { start: startsAt, end: endsAt });
      return endsAt ? `${startsAt} – ${endsAt}` : startsAt;
    }

    if (startsAt && endsAt) {
      const template = asText(labels['coupon-valid-range']);
      if (template) return fillTemplate(template, { start: startsAt, end: endsAt });
      return `${startsAt} – ${endsAt}`;
    }

    return endsAt || startsAt || '—';
  }

  function couponStatusText(status, isAvailable, labels) {
    if (status === 'expired') return asText(labels['coupon-expired-badge']) || asText(labels['coupon-expired']);
    if (status === 'used') return asText(labels['coupon-used']);
    return status && !isAvailable ? asText(labels['coupon-unknown']) : '';
  }

  function couponCombinableText(coupon, labels) {
    const values = [];
    if (coupon?.notStackable === true) {
      values.push(labels['not-stackable'] || '—');
    }
    if (isRecord(coupon?.combinesWith)) {
      const enabled = Object.entries(coupon.combinesWith)
        .filter(([, enabledForType]) => enabledForType === true)
        .map(([type]) => labels[`combines-${type}`] || type);
      if (enabled.length) values.push(enabled.join(', '));
    }
    return values.join(' · ');
  }

  function setCouponText(card, selector, value) {
    const node = card.querySelector(selector);
    if (node) node.textContent = asText(value) || '—';
  }

  function customerVisibleCouponText(value) {
    return asText(value).replace(/\uFFFD/g, '').replace(/\s+/g, ' ').trim();
  }

  function couponGrantedAt(coupon) {
    const value = asText(coupon?.grantedAt);
    const parsed = new Date(value);
    if (!value || Number.isNaN(parsed.getTime())) return null;
    return { dateTime: value, text: formatDate(value) };
  }

  function couponSourceText(source) {
    if (typeof source === 'string') return customerVisibleCouponText(source);
    if (!isRecord(source)) return '';
    return [source.label, source.title, source.name]
      .map(customerVisibleCouponText)
      .find(Boolean) || '';
  }

  function renderCouponProvenance(card, coupon) {
    const container = card.querySelector('[data-pc-coupon-provenance]');
    const grantedOutput = card.querySelector('[data-pc-coupon-granted]');
    const grantedValue = card.querySelector('[data-pc-coupon-granted-at]');
    const sourceOutput = card.querySelector('[data-pc-coupon-source]');
    const sourceValue = card.querySelector('[data-pc-coupon-source-value]');
    const grantedAt = couponGrantedAt(coupon);
    const source = couponSourceText(coupon?.source);

    if (grantedValue) {
      grantedValue.textContent = grantedAt?.text || '';
      if (grantedAt) grantedValue.dateTime = grantedAt.dateTime;
      else grantedValue.removeAttribute('datetime');
    }
    if (grantedOutput) grantedOutput.hidden = !grantedAt;
    if (sourceValue) sourceValue.textContent = source;
    if (sourceOutput) sourceOutput.hidden = !source;
    if (container) container.hidden = !grantedAt && !source;
  }

  function couponIdentity(coupon, index) {
    const source = asText(coupon?.grantId || coupon?.code || `position-${index + 1}`);
    return source.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || `position-${index + 1}`;
  }

  function cloneCouponCard(root, coupon, labels, index) {
    const status = asText(coupon?.status);
    const template = root.querySelector(status === 'expired' ? '[data-pc-expired-coupon-template]' : '[data-pc-coupon-template]');
    const source = template?.content?.querySelector?.('[data-pc-coupon]');
    if (!source) return null;

    const card = source.cloneNode(true);
    const isAvailable = status === 'available';
    const isHistorical = status === 'used' || status === 'expired';
    // Available but not open yet. The backend has no fourth status for this - a scheduled
    // coupon really is available, just not today - so it is derived here from the dates.
    const isScheduled = isAvailable && couponNotStartedYet(coupon);
    const identity = couponIdentity(coupon, index);
    const code = asText(coupon?.code);
    card.dataset.pcCouponId = asText(coupon?.grantId);
    card.dataset.pcCouponGrantId = asText(coupon?.grantId);
    card.dataset.pcCouponStatus = status;
    card.dataset.couponCode = code;

    const benefit = couponBenefitText(coupon, labels);
    const summary = asText(coupon?.summary);
    setCouponText(card, '[data-pc-coupon-code]', code);
    setCouponText(card, '[data-pc-coupon-benefit]', benefit);
    setCouponText(card, '[data-pc-coupon-expiry]', couponExpiryText(coupon, status, labels));
    renderCouponProvenance(card, coupon);
    const summaryOutput = card.querySelector('[data-pc-coupon-summary]');
    if (summaryOutput) {
      summaryOutput.textContent = summary;
      summaryOutput.hidden = !isHistorical || !summary || summary === benefit;
    }
    const statusLabel = card.querySelector('[data-pc-coupon-status-label]');
    // A scheduled coupon gets its own badge: without one the card says "available" beside a
    // future date, which reads as a contradiction rather than as an explanation.
    const statusText = isScheduled
      ? asText(labels['coupon-scheduled-badge'])
      : couponStatusText(status, isAvailable, labels);
    if (statusLabel) {
      statusLabel.textContent = statusText;
      statusLabel.hidden = !statusText;
    }

    const copy = card.querySelector('[data-pc-action="copy-coupon"]');
    const copyStatus = card.querySelector('[data-pc-coupon-copy-status]');
    const copyStatusId = `pc-coupon-copy-${identity}-${index + 1}`;
    if (isAvailable && copy) {
      copy.dataset.pcCopyValue = code;
      copy.setAttribute('aria-describedby', copyStatusId);
      /**
       * Copying a code that checkout will refuse for another week is a trip to support.
       * Disabled rather than removed so the shopper can still see the coupon is theirs and
       * that copying is what they will do once it opens.
       */
      if (isScheduled) {
        copy.disabled = true;
        copy.setAttribute('aria-disabled', 'true');
        card.dataset.pcCouponScheduled = 'true';
      }
    }
    if (isAvailable && copyStatus) copyStatus.id = copyStatusId;

    if (!isAvailable) {
      if (status === 'expired') return card;
      copy?.remove();
      copyStatus?.remove();
      card.querySelector('.pc-coupons__meta')?.remove();
      card.querySelector('[data-pc-coupon-terms]')?.remove();
      card.classList.add('pc-coupons__card--without-terms', 'pc-coupons__card--status-only');
      return card;
    }

    setCouponText(card, '[data-pc-coupon-minimum]', minimumPurchaseText(coupon?.minimumPurchase, labels));
    setCouponText(card, '[data-pc-coupon-applies]', appliesToText(coupon?.appliesTo));

    const exclusions = asText(coupon?.exclusionsText);
    const combinable = couponCombinableText(coupon, labels);
    const exclusionsOutput = card.querySelector('[data-pc-coupon-exclusions]');
    const combinableOutput = card.querySelector('[data-pc-coupon-combinable]');
    if (exclusionsOutput) {
      exclusionsOutput.textContent = exclusions;
      exclusionsOutput.hidden = !exclusions;
    }
    if (combinableOutput) {
      combinableOutput.textContent = combinable;
      combinableOutput.hidden = !combinable;
    }
    if (!exclusions && !combinable) {
      const terms = card.querySelector('[data-pc-coupon-terms]');
      if (terms) terms.hidden = true;
      card.classList.add('pc-coupons__card--without-terms');
    }
    return card;
  }

  function renderCouponCounts(root, counts) {
    root.querySelectorAll('[data-pc-coupon-count]').forEach((node) => {
      const count = counts?.[node.dataset.pcCouponCount];
      const available = Number.isFinite(Number(count));
      node.textContent = available ? ` (${count})` : '';
      node.hidden = !available;
    });
  }

  function renderCoupons({ root, response }) {
    const state = couponsRenderState(response);
    const list = root.querySelector('[data-pc-coupon-list]');
    root.querySelectorAll('[data-pc-coupons-pagination]').forEach((node) => { node.hidden = true; });
    const envelopeData = dataOf(response?.coupons);
    const items = Array.isArray(envelopeData) ? envelopeData : [];
    renderCouponCounts(root, response?.coupons?.meta?.counts);
    if (list) list.replaceChildren();
    if (state !== 'ready') return { state };
    if (!list) return { state: 'ready' };

    const labels = labelsFrom(root, '[data-pc-wallet-label]');

    items.forEach((coupon, index) => {
      const card = cloneCouponCard(root, coupon, labels, index);
      if (card) list.appendChild(card);
    });
    return { state: 'ready' };
  }

  async function copyCoupon(event, root) {
    const action = event?.target?.closest?.('[data-pc-action="copy-coupon"]');
    if (!action || !root.contains(action)) return false;
    const value = asText(action.dataset.pcCopyValue);
    const status = action.parentElement?.querySelector('[data-pc-coupon-copy-status]');
    const labels = labelsFrom(root, '[data-pc-wallet-label]');
    const copyMessage = (key) => asText(labels[key]).replace(/\{\{\s*code\s*\}\}/g, value);
    if (!value || !navigator.clipboard?.writeText) {
      if (status) status.textContent = copyMessage('copy-fallback');
      return true;
    }
    try {
      await navigator.clipboard.writeText(value);
      if (status) status.textContent = copyMessage('copy-success');
    } catch (_error) {
      if (status) status.textContent = copyMessage('copy-fallback');
    }
    return true;
  }

  function actionName(event, action) {
    const fromAction = typeof action === 'string'
      ? action
      : action?.dataset?.pcAction || action?.action;
    return asText(fromAction || event?.target?.closest?.('[data-pc-action]')?.dataset?.pcAction);
  }

  function creditsAction({ event, action, root }) {
    const name = actionName(event, action);
    const state = creditStateFor(root);
    if (name === 'retry-credits') {
      event?.preventDefault?.();
      return { view: 'credits', params: { pageSize: CREDIT_PAGE_SIZE, includePending: true, transactionFilter: state.transactionFilter, cursor: null } };
    }
    if (name === 'credit-transaction-filter') {
      event?.preventDefault?.();
      const target = action || event?.target?.closest?.('[data-pc-action="credit-transaction-filter"]');
      const requestedFilter = creditTransactionFilterFor(target?.dataset?.pcCreditFilter);
      state.transactionFilter = state.transactionFilter === requestedFilter ? 'all' : requestedFilter;
      const labels = labelsFrom(root, '[data-pc-wallet-label]');
      renderCreditTransactionFilters(root, state.transactionFilter);
      renderTransactions(root, state.transactions, labels, state.transactionFilter);
      return { handled: true };
    }
    if (name !== 'credit-page') return false;
    event?.preventDefault?.();
    const target = action || event?.target?.closest?.('[data-pc-action="credit-page"]');
    const direction = asText(target?.dataset?.pcPageDirection);
    if (direction === 'next' && state.pageInfo?.hasNextPage === true && asText(state.pageInfo?.nextCursor)) {
      return { view: 'credits', params: { pageSize: CREDIT_PAGE_SIZE, includePending: state.includePending, transactionFilter: state.transactionFilter, cursor: state.pageInfo.nextCursor } };
    }
    if (direction === 'previous' && state.cursorIndex > 0) {
      return { view: 'credits', params: { pageSize: CREDIT_PAGE_SIZE, includePending: state.includePending, transactionFilter: state.transactionFilter, cursor: state.cursorHistory[state.cursorIndex - 1] } };
    }
    return { handled: true };
  }

  function disconnectCredits({ root } = {}) {
    const key = root && (typeof root === 'object' || typeof root === 'function') ? root : fallbackCreditRoot;
    const state = creditStates.get(key);
    if (state) state.intent += 1;
    creditStates.delete(key);
  }

  function selectedCouponStatus(root) {
    const selected = root?.querySelector?.('[data-pc-filter]:checked');
    const status = asText(selected?.value);
    return COUPON_STATUSES.has(status) ? status : 'available';
  }

  function couponsAction({ event, action, root }) {
    const name = actionName(event, action);
    if (name === 'retry-coupons') {
      event?.preventDefault?.();
      return { view: 'coupons', params: { status: selectedCouponStatus(root) } };
    }
    if (name === 'copy-coupon') return copyCoupon(event, root);
    return false;
  }

  modules.credits = {
    load: loadCredits,
    render: renderCredits,
    handleAction: creditsAction,
    disconnect: disconnectCredits
  };

  modules.coupons = {
    load: loadCoupons,
    render: renderCoupons,
    handleAction: couponsAction,
    disconnect() {}
  };
})();
